// AIOE-owned; not mirrored by the workbench (it receives the result as `approvals.remoteApprovals` in GET /policy/v1).
/**
 * What a phone or the console may approve, by organisation and by person. The workbench's policy says per risk level
 * whether a remote client (phone, console, relay) may approve a tool call or only the machine itself
 * (`approvals.remoteApprovals`, low and medium allowed and high kept to the machine by default). The organisation's
 * policy sets that; for each level it may instead let each person decide for their own machines. AIOE then answers
 * each person's workbench with the values that apply to them, so the workbench needs nothing new for a published
 * policy. A node is shared, so it always follows the organisation's values.
 *
 *   GET/PUT /admin/v1/remote-approvals   (administrator)  RemoteApprovalRules
 *   GET/PUT /me/v1/remote-approvals      (the person)     RemoteApprovalsView / RemoteApprovalChoices
 */
import { z } from "zod";
export const REMOTE_APPROVAL_RISKS = ["low", "medium", "high"];
export const remoteApprovalRiskSchema = z.enum(REMOTE_APPROVAL_RISKS);
export const remoteApprovalValueSchema = z.enum(["allow", "deny"]);
/** One value per level, as `approvals.remoteApprovals` carries them. */
export const remoteApprovalValuesSchema = z.object({ low: remoteApprovalValueSchema, medium: remoteApprovalValueSchema, high: remoteApprovalValueSchema });
/** The workbench's own defaults (its policyFile.ts), which apply when no policy says otherwise. */
export const DEFAULT_REMOTE_APPROVALS = { low: "allow", medium: "allow", high: "deny" };
/** The organisation's rule: the levels each person decides for their own machines. The rest follow the policy. */
export const remoteApprovalRulesSchema = z.object({
    personDecides: z.array(remoteApprovalRiskSchema).max(3).default([]).transform((l) => REMOTE_APPROVAL_RISKS.filter((r) => l.includes(r))),
});
/** A person's own choices, per level; a level the organisation does not leave to them is ignored. */
export const remoteApprovalChoicesSchema = z.object({
    low: remoteApprovalValueSchema.optional(),
    medium: remoteApprovalValueSchema.optional(),
    high: remoteApprovalValueSchema.optional(),
});
/** `GET /me/v1/remote-approvals`: everything a screen needs to show and change the person's choice. */
export const remoteApprovalsViewSchema = z.object({
    /** What the organisation's policy says (the published policy's values, else the defaults). */
    organisation: remoteApprovalValuesSchema,
    /** The levels the organisation leaves to each person. */
    personDecides: z.array(remoteApprovalRiskSchema),
    /** The person's own choices (only levels they may decide). */
    choices: remoteApprovalChoicesSchema,
    /** What applies to the person's own workbenches. */
    effective: remoteApprovalValuesSchema,
});
/** The organisation's values, then the person's choices where the rules leave a level to them. */
export function resolveRemoteApprovals(organisation, rules, choices) {
    const out = { ...organisation };
    for (const r of rules?.personDecides ?? []) {
        const c = choices?.[r];
        if (c)
            out[r] = c;
    }
    return out;
}
/** `aad` for allow, allow, deny: the revision tag a policy answer carries once these values were resolved for a person. */
export const remoteApprovalsTag = (v) => REMOTE_APPROVAL_RISKS.map((r) => (v[r] === "allow" ? "a" : "d")).join("");
//# sourceMappingURL=remoteApprovals.js.map